import { el, onTeardown } from '../lib/dom.js';
import { settings, applyTheme } from '../store/settings.js';
import { selection, importFlags, replaceFlags, normalizeSelection } from '../store/selection.js';
import { prettify } from '../data/pokedex-resolver.js';
import { chooseDialog } from '../lib/dialog.js';
import { team } from '../store/team.js';
import { formTracking, markFormsCaught } from '../store/formTracking.js';
import { shinyHunts } from '../store/shinyHunts.js';
import { playedGames, markPlayed } from '../store/playedGames.js';
import { parseSaveDex } from '../lib/savedex.js';
import { canInstall, onInstallChange, promptInstall } from '../lib/install.js';

export async function SettingsView() {
  const view = el('section', { class: 'view settings' });
  const st = settings.get();

  const themeField = selectField(
    'Theme',
    st.theme,
    [
      ['system', 'System'],
      ['light', 'Light'],
      ['dark', 'Dark'],
      ['black', 'Black (OLED)'],
      ['sepia', 'Sepia'],
    ],
    (v) => {
      settings.set({ theme: v });
      applyTheme();
    },
  );

  const ACCENTS = [
    ['red', '#b3161a'],
    ['blue', '#2f6fed'],
    ['green', '#1f9d57'],
    ['amber', '#d98a00'],
    ['violet', '#7b52e0'],
    ['rose', '#e0417a'],
  ];
  const accentRow = el(
    'div',
    { class: 'swatches' },
    ...ACCENTS.map(([key, hex]) =>
      el('button', {
        class: `swatch${st.accent === key ? ' is-on' : ''}`,
        type: 'button',
        style: `--sw:${hex}`,
        title: key,
        'aria-label': `${key} accent`,
        onclick: () => {
          settings.set({ accent: key });
          applyTheme();
          [...accentRow.children].forEach((b, i) =>
            b.classList.toggle('is-on', ACCENTS[i][0] === key),
          );
        },
      }),
    ),
  );
  const accentField = el('div', { class: 'field' }, el('span', {}, 'Accent color'), accentRow);

  const fontSizeField = selectField(
    'Text size',
    st.fontScale || 'default',
    [
      ['small', 'Small'],
      ['default', 'Default'],
      ['large', 'Large'],
    ],
    (v) => {
      settings.set({ fontScale: v });
      applyTheme();
    },
  );

  const reduceMotionField = el(
    'label',
    { class: 'field field--check' },
    el('input', {
      type: 'checkbox',
      checked: !!st.reduceMotion,
      onchange: (e) => {
        settings.set({ reduceMotion: e.target.checked });
        applyTheme();
      },
    }),
    'Reduce motion',
  );

  const spriteField = selectField(
    'Sprite style',
    st.spriteStyle,
    [
      ['default', 'Pixel (modern)'],
      ['game', 'Game era (pixel art from the selected game)'],
      ['official', 'Official artwork'],
      ['home', 'Pokémon HOME'],
    ],
    (v) => settings.set({ spriteStyle: v }),
  );

  const shinyField = el(
    'label',
    { class: 'field field--check' },
    el('input', {
      type: 'checkbox',
      checked: st.showShiny,
      onchange: (e) => settings.set({ showShiny: e.target.checked }),
    }),
    'Default detail view to shiny sprites',
  );

  const teamNavField = el(
    'label',
    { class: 'field field--check' },
    el('input', {
      type: 'checkbox',
      checked: st.showTeamNav !== false,
      onchange: (e) => settings.set({ showTeamNav: e.target.checked }),
    }),
    'Show Team in the navigation bar',
  );

  const hapticsField = el(
    'label',
    { class: 'field field--check' },
    el('input', {
      type: 'checkbox',
      checked: st.haptics !== false,
      onchange: (e) => settings.set({ haptics: e.target.checked }),
    }),
    'Haptic feedback on catch',
  );

  const defaultRouteField = selectField(
    'Landing screen',
    st.defaultRoute || 'dex',
    [
      ['dex', 'Dex'],
      ['team', 'Team'],
      ['search', 'Search'],
    ],
    (v) => settings.set({ defaultRoute: v }),
  );

  const storageNote = el('p', { class: 'settings__note' }, 'Calculating storage…');
  if (navigator.storage?.estimate) {
    navigator.storage.estimate().then(({ usage = 0, quota = 0 }) => {
      const mb = (n) => (n / 1024 / 1024).toFixed(1);
      storageNote.textContent = `Using ${mb(usage)} MB of ~${mb(quota)} MB available (caches + tracking data).`;
    });
  } else {
    storageNote.textContent = 'Storage estimate not available in this browser.';
  }

  // Import the Pokédex out of a Gen 1/2/3 game save (.sav / .srm).
  const saveNote = el('p', { class: 'settings__note' });
  const saveInput = el('input', {
    type: 'file',
    accept: '.sav,.srm,.dsv,.sa1,.fla,application/octet-stream',
    style: 'display:none',
    onchange: async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      saveNote.textContent = 'Reading…';
      let dex;
      try {
        dex = parseSaveDex(await file.arrayBuffer());
      } catch (err) {
        saveNote.textContent = err.message || 'Could not read that save file.';
        e.target.value = '';
        return;
      }
      const who = dex.trainer || dex.game;
      // Which game bucket this save's catches belong to. The parser can't
      // always tell (an RSE save could be Ruby or Emerald) — prefer the
      // game the user currently has selected if it's one of the candidates.
      const cands = dex.versionGroups || [];
      const target = cands.includes(settings.get().versionGroup)
        ? settings.get().versionGroup
        : cands[0];
      const unown = dex.formCaught?.length || 0;
      const unownLine = unown ? ` · ${unown} Unown letter${unown === 1 ? '' : 's'}` : '';
      const choice = await chooseDialog({
        title: `${dex.game}${dex.trainer ? ` · ${dex.trainer}` : ''}`,
        body: `${dex.seen.length} seen · ${dex.caught.length} caught${unownLine} in this save.`,
        choices: [
          { key: 'merge', label: `Add to ${prettify(target)}` },
          {
            key: 'replace',
            label: `Replace ${prettify(target)} with this`,
            class: 'button--danger',
          },
          { key: null, label: 'Cancel', class: 'button--ghost' },
        ],
      });
      if (choice === 'merge') {
        importFlags({ seen: dex.seen, caught: dex.caught, game: target });
        markFormsCaught(dex.formCaught);
        markPlayed(...cands);
        saveNote.textContent = `Added ${dex.caught.length} caught / ${dex.seen.length} seen${unownLine} to ${prettify(target)}.`;
      } else if (choice === 'replace') {
        replaceFlags({ seen: dex.seen, caught: dex.caught, game: target });
        markFormsCaught(dex.formCaught);
        markPlayed(...cands);
        saveNote.textContent = `Set ${prettify(target)}'s Pokédex from ${who}${unownLine ? ` (${unown} Unown letters)` : ''}.`;
      } else {
        saveNote.textContent = '';
      }
      e.target.value = '';
    },
  });

  const importInput = el('input', {
    type: 'file',
    accept: 'application/json',
    style: 'display:none',
    onchange: async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const data = JSON.parse(await file.text());
        if (data.settings) settings.replace(data.settings);
        if (data.selection) selection.replace(normalizeSelection(data.selection));
        if (data.team) team.replace(data.team);
        if (data.formTracking) formTracking.replace(data.formTracking);
        if (data.shinyHunts) shinyHunts.replace(data.shinyHunts);
        if (data.playedGames) playedGames.replace(data.playedGames);
        await chooseDialog({
          title: 'Import complete',
          body: 'Your backup was restored.',
          choices: [{ key: null, label: 'OK' }],
        });
      } catch {
        await chooseDialog({
          title: 'Couldn’t read that file',
          body: 'It doesn’t look like a Pocketdex backup.',
          choices: [{ key: null, label: 'OK' }],
        });
      }
    },
  });

  // Install (only when the browser has offered it and the app isn't
  // already installed).
  const installRow = el('div', { class: 'settings__install' });
  function syncInstall() {
    installRow.replaceChildren(
      canInstall()
        ? el(
            'button',
            {
              class: 'button',
              type: 'button',
              onclick: () => promptInstall(),
            },
            'Install app',
          )
        : el(
            'p',
            { class: 'settings__note' },
            window.matchMedia('(display-mode: standalone)').matches
              ? 'Running as an installed app.'
              : 'Use your browser menu to add this Pokédex to your home screen.',
          ),
    );
  }
  syncInstall();
  const offInstall = onInstallChange(syncInstall);

  view.append(
    el('header', { class: 'view__header' }, el('h1', {}, 'Settings')),
    installRow,
    el(
      'div',
      { class: 'settings__group' },
      themeField,
      accentField,
      fontSizeField,
      reduceMotionField,
      spriteField,
      shinyField,
      hapticsField,
      teamNavField,
      defaultRouteField,
    ),

    el('h2', {}, 'Your data'),
    el(
      'p',
      { class: 'settings__note' },
      el('a', { class: 'link', href: '#/progress' }, 'Progress by game'),
      ' · ',
      el('a', { class: 'link', href: '#/shiny' }, 'Shiny hunts'),
    ),
    el(
      'div',
      { class: 'settings__save' },
      el(
        'button',
        { class: 'button', type: 'button', onclick: () => saveInput.click() },
        'Import from a game save',
      ),
      el('p', { class: 'settings__note' }, 'A .sav / .srm / .dsv from a Gen 1–5 game.'),
    ),
    saveInput,
    saveNote,
    storageNote,
    el(
      'div',
      { class: 'settings__actions' },
      el(
        'button',
        {
          class: 'button',
          type: 'button',
          onclick: () => exportData(),
        },
        'Export backup',
      ),
      el(
        'button',
        {
          class: 'button',
          type: 'button',
          onclick: () => importInput.click(),
        },
        'Import backup',
      ),
      importInput,
      el(
        'button',
        {
          class: 'button button--danger',
          type: 'button',
          onclick: async () => {
            const pick = await chooseDialog({
              title: 'Clear all tracking data',
              body: 'Removes seen/caught for every game plus all favorites and notes, and can’t be undone. To keep favorites and notes, use “Reset caught / seen” on the Progress screen instead.',
              choices: [
                { key: 'clear', label: 'Clear everything', class: 'button--danger' },
                { key: null, label: 'Cancel', class: 'button--ghost' },
              ],
            });
            if (pick === 'clear') selection.replace({ version: 3, pokemon: {}, games: {} });
          },
        },
        'Clear tracking data',
      ),
      el(
        'button',
        {
          class: 'button button--danger',
          type: 'button',
          onclick: async () => {
            if (!('caches' in window)) return;
            const pick = await chooseDialog({
              title: 'Clear cached data',
              body: 'Drops cached PokéAPI responses and sprites. They re-download next time you’re online.',
              choices: [
                { key: 'clear', label: 'Clear cache', class: 'button--danger' },
                { key: null, label: 'Cancel', class: 'button--ghost' },
              ],
            });
            if (pick !== 'clear') return;
            for (const key of await caches.keys()) await caches.delete(key);
            await chooseDialog({
              title: 'Cache cleared',
              body: '',
              choices: [{ key: null, label: 'OK' }],
            });
          },
        },
        'Clear cached API data',
      ),
    ),

    el('h2', {}, 'About'),
    el(
      'p',
      { class: 'settings__note' },
      'Data from ',
      el(
        'a',
        { href: 'https://pokeapi.co/', class: 'link', target: '_blank', rel: 'noreferrer' },
        'PokéAPI',
      ),
      '. Tracking data and preferences are stored locally in your browser.',
    ),
  );

  onTeardown(view, () => {
    offInstall();
  });
  return view;
}

function selectField(label, value, options, onChange) {
  const select = el(
    'select',
    { onchange: (e) => onChange(e.target.value) },
    ...options.map(([v, text]) => el('option', { value: v, selected: v === value }, text)),
  );
  return el('label', { class: 'field' }, el('span', {}, label), select);
}

function exportData() {
  const payload = {
    exportedAt: new Date().toISOString(),
    settings: settings.get(),
    selection: selection.get(),
    team: team.get(),
    formTracking: formTracking.get(),
    shinyHunts: shinyHunts.get(),
    playedGames: playedGames.get(),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = el('a', {
    href: url,
    download: `pokedex-backup-${new Date().toISOString().slice(0, 10)}.json`,
  });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
